import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';

const HelpSupportScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const content = {
    bn: {
      title: 'সাহায্য এবং সহায়তা',
      faq: 'প্রায়শই জিজ্ঞাসিত প্রশ্ন',
      contactSupport: 'সহায়তার জন্য যোগাযোগ',
      faqs: [
        {
          question: 'আমি কীভাবে আমার পাসওয়ার্ড রিসেট করব?',
          answer: 'লগইন পৃষ্ঠায় "পাসওয়ার্ড ভুলে গেছেন?" লিঙ্কে ক্লিক করুন এবং নির্দেশাবলী অনুসরণ করুন।'
        },
        {
          question: 'আমি কীভাবে আমার অগ্রগতি ট্র্যাক করব?',
          answer: 'আপনার প্রোফাইল পৃষ্ঠায় যান আপনার শেখার পরিসংখ্যান, স্ট্রিক এবং সম্পূর্ণ পাঠ দেখতে।'
        },
        {
          question: 'কুইজে কতগুলি প্রশ্ন আছে?',
          answer: 'প্রতিটি কুইজে বিভিন্ন সংখ্যক প্রশ্ন থাকে, সাধারণত 5-20টি প্রশ্ন পাঠের উপর নির্ভর করে।'
        },
        {
          question: 'আমি কি অফলাইনে শিখতে পারি?',
          answer: 'বর্তমানে, অ্যাপটি সম্পূর্ণ কার্যকারিতার জন্য ইন্টারনেট সংযোগ প্রয়োজন।'
        },
        {
          question: 'আমি কীভাবে ভাষা পরিবর্তন করব?',
          answer: 'সেটিংস → ভাষাতে যান এবং আপনার পছন্দসই ভাষা নির্বাচন করুন (বাংলা, ইতালিয়ান, বা ইংরেজি)।'
        }
      ],
      supportOptions: [
        { icon: '📧', title: 'ইমেইল সহায়তা', description: 'আমাদের একটি ইমেইল পাঠান' },
        { icon: '💬', title: 'লাইভ চ্যাট', description: 'আমাদের টিমের সাথে চ্যাট করুন' },
        { icon: '👤', title: 'Facebook', description: 'Facebook-এ আমাদের সাথে যোগাযোগ করুন' }
      ]
    },
    it: {
      title: 'Aiuto e Supporto',
      faq: 'Domande Frequenti',
      contactSupport: 'Contatta il Supporto',
      faqs: [
        {
          question: 'Come posso reimpostare la mia password?',
          answer: 'Clicca su "Password dimenticata?" nella pagina di login e segui le istruzioni.'
        },
        {
          question: 'Come posso tracciare i miei progressi?',
          answer: 'Vai alla tua pagina Profilo per vedere le tue statistiche di apprendimento, serie e lezioni completate.'
        },
        {
          question: 'Quante domande ci sono in un quiz?',
          answer: 'Ogni quiz ha un numero variabile di domande, di solito 5-20 domande a seconda della lezione.'
        },
        {
          question: 'Posso imparare offline?',
          answer: 'Attualmente, l\'app richiede una connessione internet per la piena funzionalità.'
        },
        {
          question: 'Come cambio la lingua?',
          answer: 'Vai a Impostazioni → Lingua e seleziona la tua lingua preferita (Bengalese, Italiano o Inglese).'
        }
      ],
      supportOptions: [
        { icon: '📧', title: 'Supporto Email', description: 'Inviaci un\'email' },
        { icon: '💬', title: 'Chat Live', description: 'Chatta con il nostro team' },
        { icon: '👤', title: 'Facebook', description: 'Contattaci su Facebook' }
      ]
    },
    en: {
      title: 'Help & Support',
      faq: 'Frequently Asked Questions',
      contactSupport: 'Contact Support',
      faqs: [
        {
          question: 'How do I reset my password?',
          answer: 'Click on "Forgot Password?" on the login page and follow the instructions.'
        },
        {
          question: 'How can I track my progress?',
          answer: 'Go to your Profile page to view your learning statistics, streak, and completed lessons.'
        },
        {
          question: 'How many questions are in a quiz?',
          answer: 'Each quiz has a varying number of questions, typically 5-20 questions depending on the lesson.'
        },
        {
          question: 'Can I learn offline?',
          answer: 'Currently, the app requires an internet connection for full functionality.'
        },
        {
          question: 'How do I change the language?',
          answer: 'Go to Settings → Language and select your preferred language (Bengali, Italian, or English).'
        }
      ],
      supportOptions: [
        { icon: '📧', title: 'Email Support', description: 'Send us an email' },
        { icon: '💬', title: 'Live Chat', description: 'Chat with our team' },
        { icon: '👤', title: 'Facebook', description: 'Contact us on Facebook' }
      ]
    }
  };

  const text = content[currentLanguage];

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="help-screen">
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{text.title}</h1>
        </div>
        <div className="header-btn-placeholder"></div>
      </div>

      <LessonBannerAd />

      <div className="help-content">
        {/* FAQ Section */}
        <div className="help-section">
          <h2>{text.faq}</h2>
          <div className="faq-list">
            {text.faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                onClick={() => toggleFaq(index)}
              >
                <div className="faq-question">
                  <span>{faq.question}</span>
                  <span className="faq-icon">{expandedFaq === index ? '−' : '+'}</span>
                </div>
                {expandedFaq === index && (
                  <div className="faq-answer">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Support Section */}
        <div className="help-section">
          <h2>{text.contactSupport}</h2>
          <div className="support-options">
            <a href="https://shikhunitalian.com/" target="_blank" rel="noopener noreferrer" className="support-card">
              <svg className="support-icon-svg" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              <h3>Website</h3>
              <p>shikhunitalian.com</p>
            </a>
            
            <a href="tel:+393273937479" className="support-card">
              <svg className="support-icon-svg" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
              </svg>
              <h3>Phone</h3>
            </a>
            
            <a href="https://wa.me/393273937479" target="_blank" rel="noopener noreferrer" className="support-card">
              <svg className="support-icon-svg" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <h3>WhatsApp</h3>
              <p>Chat with us</p>
            </a>

            <a href="https://www.facebook.com/shikhunitalian" target="_blank" rel="noopener noreferrer" className="support-card">
              <svg className="support-icon-svg" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <h3>Facebook</h3>
              <p>Message us</p>
            </a>
          </div>

          {/* Developer Contact */}
          <div className="developer-contact">
            <h3>Developer Support</h3>
            <p>Ronaldo Roy</p>
            <div className="developer-links">
              <a href="https://www.ronaldoroy.com" target="_blank" rel="noopener noreferrer" className="dev-link">
                <svg className="dev-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                <span>ronaldoroy.com</span>
              </a>
              <a href="tel:+393273937479" className="dev-link">
                <svg className="dev-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                </svg>
                <span>+39 327 393 7479</span>
              </a>
              <a href="https://wa.me/393273937479" target="_blank" rel="noopener noreferrer" className="dev-link">
                <svg className="dev-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default HelpSupportScreen;

